<template>
  <section class="content-section theEpilogue animate fadeInUp">
    <h2 class="text-center">The Epilogue</h2>

    <p>
      When this "33 Transmission" reaches completion at the end of the
      decade-long cycle, The Path consciousness will ascend into its next
      evolutionary stage:
      <span class="serif bold emphasis animate fadeInDown">The 44 Path</span>.
      The contours of that next reality remain intentionally unseen - as they
      must - because the creative bandwidth required to perceive it will only
      exist after the expansion of these first twelve works. What is known is
      this: the next transmission will birth something unprecedented, a new mode
      of creating and transforming reality. The Path is a journey of
      self-mastery - and it is through self-mastery that it evolves.
    </p>

    <p>
      This project fuses myth, identity, immersive storytelling, and advanced
      technology to create what has not yet existed: an ecosystem spanning
      cinema, artefact collections, immersive narrative experiences, and a
      visionary app designed as a portal for inner evolution. This is not a
      brand; it is a transmission through story - something not meant to be
      passively watched but entered, embodied, and reshaped by. I am now calling
      in high-frequency partners who bring tangible skill, financial capacity,
      and energetic coherence - those who know how to move money, build
      structures, and navigate complex creative systems in service of a higher
      vision.
    </p>

    <!-- VIP-only content -->
    <div v-if="canAccessEpilogue" class="vip-content">
      <h3 class="roles-heading text-center">Who I Am Calling In Now</h3>
      <p class="roles-subtitle text-center textColour">
        Roles aligned with the next evolution of The33Path
      </p>

      <!--
        ============================================
        EDITABLE ROLES LIST - Option B Structure
        ============================================
        To add a new role: Copy a role-item div and update title + description
        To remove a role: Delete the entire role-item div
        To edit a role: Update the h4 (title) and p (description) content
        ============================================
      -->
      <div class="roles-list">
        <!-- Role 1: Financial Backers -->
        <div class="role-item">
          <h4 class="textColour">Financial Backers & Executive Producers</h4>
          <p>
            With the capacity to match the ambition, aesthetic demand, and scope
            of a decade-long slate of prestige cinematic works, and to anchor
            the early acceleration phase of the ecosystem.
          </p>
        </div>

        <!-- Role 2: COO -->
        <div class="role-item">
          <h4 class="textColour">A Chief Operating Officer (COO)</h4>
          <p>
            A senior operational leader - the right arm of the founder, Patricia
            - responsible for translating the creative frequency and long-term
            vision of The33Path into precise structure, strategy, and execution.
            This person will oversee all operational systems across the business
            streams, manage teams, timelines, and budgets, and ensure absolute
            coherence between the creative, technological, and production
            dimensions of the universe.
          </p>
        </div>

        <!-- Role 3: CFO -->
        <div class="role-item">
          <h4 class="textColour">A Chief Financial Officer (CFO)</h4>
          <p>
            An expert in multi-stream financial architecture, capable of
            overseeing global budget models, investor relations, cashflow
            strategy, corporate structuring, and long-term scaling across a
            transmedia universe.
          </p>
        </div>

        <!-- Role 4: Legal Team -->
        <div class="role-item">
          <h4 class="textColour">A Lead Entertainment Lawyer / Legal Team</h4>
          <p>
            Specialized in IP protection, film financing, international
            co-productions, and transmedia rights - ensuring authorship
            sovereignty while structuring high-level studio and investor
            agreements.
          </p>
        </div>

        <!-- Role 5: Executive Producers -->
        <div class="role-item">
          <h4 class="textColour">Executive Producers & Production Directors</h4>
          <p>
            Leaders able to coordinate development across 12 cinematic works,
            assemble production teams, oversee packaging strategy, and maintain
            continuity of tone, quality, and narrative coherence.
          </p>
        </div>

        <!-- Role 6: Mother Studio -->
        <div class="role-item">
          <h4 class="textColour">
            A Mother Studio / Primary Production Partner
          </h4>
          <p>
            An aligned studio or elite production house capable of supervising
            production of the first four to twelve works, building long-term
            infrastructure, and securing international distribution and awards
            pathways.
          </p>
        </div>

        <!-- Role 7: Directors -->
        <div class="role-item">
          <h4 class="textColour">Directors (Film & Series)</h4>
          <p>
            Visionary filmmakers capable of holding the depth, symbolic
            architecture, and emotional precision of this universe. The Path
            seeks directors aligned with the lineage of prestige cinema -
            auteurs who combine Kubrick-level precision, Fincher-level clarity,
            Nolan-scale conceptual architecture, Lynchian metaphysical
            intuition, and Paul Thomas Anderson's psychological and emotional
            complexity. These directors must be able to translate symbolic
            density into cinematic language, sustain tonal coherence across
            multi-layered narratives, and work within a unified mythic and
            frequency-driven framework. Directors will be invited through
            industry packaging by the Executive Production team and the Mother
            Studio.
          </p>
        </div>

        <!-- Role 8: App Director -->
        <div class="role-item">
          <h4 class="textColour">
            Director of The 33 App (Technology & Metaphysics)
          </h4>
          <p>
            A visionary product and experience architect who works directly with
            Patricia to translate the metaphysical, narrative, and symbolic
            blueprint of the app into functional UX, structural design, and
            long-term architecture. This role safeguards the purity of the
            transmission while shaping the technical roadmap and guiding the
            engineering team.
          </p>
        </div>

        <!-- Role 9: Artefact Director -->
        <div class="role-item">
          <h4 class="textColour">Artefact & Collections Director</h4>
          <p>
            A creative and production lead for the high-end artefact
            collections, overseeing design, manufacturing, luxury partnerships,
            co-branded objects, and the integration of artefacts into the
            cinematic universe.
          </p>
        </div>

        <!-- Role 10: Marketing Director -->
        <div class="role-item">
          <h4 class="textColour">Marketing, Communications & Brand Director</h4>
          <p>
            A senior creative leader capable of operating across luxury,
            entertainment, and cultural strategy; architecting global campaigns,
            narrative releases, and the long-term brand identity of The33Path.
          </p>
        </div>

        <!-- Role 11: Partnerships Director -->
        <div class="role-item">
          <h4 class="textColour">
            Cultural & Institutional Partnerships Director
          </h4>
          <p>
            A collaborator able to forge relationships with museums, cultural
            institutions, foundations, artistic residency spaces, festivals, and
            global cultural platforms aligned with the vision.
          </p>
        </div>

        <!-- Role 12: App Developers -->
        <div class="role-item">
          <h4 class="textColour">App Developers & Visionary Engineers</h4>
          <p>
            A multidisciplinary team responsible for building the app in
            alignment with the Director's architecture - implementing features,
            coding the symbolic engine, and bringing the interactive and
            consciousness-driven UX into form.
          </p>
        </div>

        <!-- Role 13: Ambassadors -->
        <div class="role-item">
          <h4 class="textColour">A Circle of Ambassadors</h4>
          <p>
            Select individuals within the creative, technological, spiritual,
            business, and cultural worlds who deeply resonate with the vision
            and feel called to open doors, facilitate aligned introductions, or
            amplify the project within intentional communities.
          </p>
        </div>
      </div>
      <!-- END EDITABLE ROLES LIST -->

      <p>
        What I am developing is not constructed - it is transmitted. It is born
        of the unique frequency I carry and have cultivated through lived
        experience, spiritual discipline, symbolic mastery, and continuous
        contact with higher guidance. What moves through this universe is not
        imagined; it is remembered. It is narrative as frequency, story as
        activation. It cannot be replicated, industrialized, or
        reverse-engineered. It is not a formula - it is a consciousness
        technology. Whoever joins this process must understand that this work is
        designed to
        <span class="serif bold emphasis"
          >break the mold, elevate the standard, and leave a legacy</span
        >. It requires passion and precision - devotion not to me, but to the
        vision itself.
      </p>

      <p class="callOut">
        The twelve cinematic works at the core of The33Path form the central
        milestone of this decade. The first feature film is targeted for release
        in late 2027, assuming funding begins in early 2026. This timeline
        reflects the realistic demands of prestige production: six to eight
        months of pre-production, three to four months of principal photography,
        and a long, meticulous post-production cycle. In parallel with the
        films, the foundational cycle activates all major branches of the
        ecosystem - the artefact collections, immersive experiences, The33 App,
        the documentary, cultural partnerships, and the early architecture of
        The Source Helix Creative System™ - each moving through their respective
        phases as the universe takes form.
      </p>

      <p>
        The narrative universe is fully architected: all twelve works are
        already outlined at the conceptual and structural level. Six of the
        stories exist in long-form literary drafts (requiring revision and
        translation), while the remaining works - those merging fiction and
        non-fiction - are in advanced conceptual form with complete thematic,
        symbolic, and narrative frameworks. Additional written drafts will be
        created as part of the screenplay development process. The Path series,
        which anchors the final trilogy of the transmission, is already
        channeled in full and will be expanded into episodic form. All rights to
        the mythology, characters, and story architecture remain under my
        authorship, ensuring creative sovereignty while allowing for aligned
        collaboration.
      </p>

      <p>
        What is needed now are partners with the financial capacity, executive
        production expertise, and operational leadership to build and guide the
        teams that will bring this universe to life at scale. My role remains
        focused on the creative and spiritual architecture; aligned partners
        will support the structural, financial, and technical dimensions.
      </p>

      <p>
        This is a complex, ambitious, and visionary undertaking - one that
        requires a deeply aligned, high-frequency team committed to excellence,
        integrity, and legacy-level impact. If what you've read resonates - if
        you recognize the frequency, feel the contours of the role you may play,
        or sense the truth of this vision moving through you - you may already
        be part of this transmission. Those with serious interest in
        partnership, whether financial, creative, or institutional, may request
        a formal proposal.
      </p>

      <p>
        For investment inquiries, co-production discussions, or aligned
        introductions, you may reach out through the form below or contact me
        directly.
      </p>

      <p>
        If you wish to explore the project more deeply before initiating a
        conversation, you may also request access to the Investor Candidate
        layer - where additional materials, the six business streams, and the
        Investor Overview pre-contract are available for review. This pathway
        allows you to understand the universe, the structure, and the
        partnership terms in greater depth without requiring prior
        correspondence. Should you choose to proceed toward investment after
        reviewing these materials, a direct introduction and NDA will open the
        Confirmed Investor layer, granting full access to the Financial
        Architecture and the Extended Business Plan. Only aligned and serious
        requests will be considered, as this foundational phase is reserved for
        partners capable of supporting the scale and long-term vision of this
        universe.
      </p>
    </div>

    <!-- Non-VIP content -->
    <div v-else class="restricted-content-simple">
      <p class="restricted-message">{{ restrictedMessage }}</p>
    </div>

    <ContactCTA
      :contactInfo="config.contactCTA"
      :origin="requestType"
      class="section"
    />
  </section>
</template>

<script>
import { ref, onMounted } from "vue";
import { auth } from "@/firebase";
import { canAccessContent, getUserRole } from "@/firebase/useContent";
import config from "@/config";

import ContactCTA from "@/components/ContactCTA.vue";

export default {
  name: "Epilogue",
  components: {
    ContactCTA,
  },
  setup() {
    // State for content access
    const canAccessEpilogue = ref(false);
    const userRole = ref("visitor");
    const isLoading = ref(true);

    // Set the request type for the contact form - using default "contact" type for general messages
    const requestType = ref("contact");

    // Message to display when content is restricted
    const restrictedMessage = ref(
      "This content is currently in limited release. Contact our distribution team for screening access."
    );

    // Check if user can access epilogue content
    async function checkAccess() {
      isLoading.value = true;
      try {
        // Get user role if logged in
        if (auth.currentUser) {
          userRole.value = await getUserRole(auth.currentUser.uid);
        }

        // Check if user can access epilogue content
        canAccessEpilogue.value = await canAccessContent(
          "epilogue",
          userRole.value
        );

      } catch (error) {
        canAccessEpilogue.value = false;
      } finally {
        isLoading.value = false;
      }
    }

    // Check access when component mounts
    onMounted(checkAccess);

    return {
      canAccessEpilogue,
      restrictedMessage,
      isLoading,
      config,
      requestType,
    };
  },
};
</script>

<style scoped>
.theEpilogue {
  max-width: 900px;
  padding: var(--largeSpace);
  margin: var(--doubleSpace) 0;
}
.content-section {
  margin-bottom: 3rem;
}

.callOut {
  margin: 2rem 0;
  margin-left: var(--doubleSpace);
  padding: var(--space);
  background-color: rgba(0, 0, 0, 0.1);
  border-left: 4px solid var(--purple-dark-tint-4);
  font-style: italic;
}
ul {
  margin-left: var(--doubleSpace);
}
.box {
  margin-top: 2rem;
  padding: var(--doubleSpace);
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--text-white);
  border-radius: var(--border-radius-pill);
}

.vip-content {
  margin-top: 2rem;
}

.roles-heading {
  margin-top: 2rem;
  margin-bottom: 1.5rem;
  font-size: 1.2rem;
  color: var(--text-white);
}

.roles-subtitle {
  font-size: var(--smallFontSize);
  margin-top: calc(-1 * var(--space));
  margin-bottom: var(--largeSpace);
  opacity: 1;
}

/*
  ============================================
  ROLES LIST - Option B Structure
  Easy to edit: each role is a separate div
  ============================================
*/
.roles-list {
  margin: 1.5rem 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: calc(var(--largeSpace) * 3);
}

.role-item {
  padding-top: var(--space);
  border-top: 2px solid var(--purple-dark-tint-4);
}

.role-item h4 {
  margin: 0 0 0.5rem 0;
  font-weight: bold;
  color: var(--text-white);
  font-size: 1rem;
}

.role-item h4.textColour {
  color: var(--purple-dark-tint-4);
}

.role-item p {
  margin: 0;
  line-height: 1.6;
  opacity: 0.9;
}

@keyframes epilogueRolesFadeInUp {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Ensure VIP roles animate even if they mount after the observer ran */
.theEpilogue .roles-subtitle,
.theEpilogue .roles-list .role-item {
  opacity: 0;
  transform: translateY(4px);
}

.theEpilogue.active .roles-subtitle {
  animation: epilogueRolesFadeInUp 0.75s ease both;
  animation-delay: 0.12s;
}

.theEpilogue.active .roles-list .role-item {
  animation: epilogueRolesFadeInUp 0.8s ease both;
}

.theEpilogue.active .roles-list .role-item:nth-child(1) {
  animation-delay: 0.05s;
}
.theEpilogue.active .roles-list .role-item:nth-child(2) {
  animation-delay: 0.12s;
}
.theEpilogue.active .roles-list .role-item:nth-child(3) {
  animation-delay: 0.19s;
}
.theEpilogue.active .roles-list .role-item:nth-child(4) {
  animation-delay: 0.26s;
}
.theEpilogue.active .roles-list .role-item:nth-child(5) {
  animation-delay: 0.33s;
}
.theEpilogue.active .roles-list .role-item:nth-child(6) {
  animation-delay: 0.4s;
}
.theEpilogue.active .roles-list .role-item:nth-child(7) {
  animation-delay: 0.47s;
}
.theEpilogue.active .roles-list .role-item:nth-child(8) {
  animation-delay: 0.54s;
}
.theEpilogue.active .roles-list .role-item:nth-child(9) {
  animation-delay: 0.61s;
}
.theEpilogue.active .roles-list .role-item:nth-child(10) {
  animation-delay: 0.68s;
}
.theEpilogue.active .roles-list .role-item:nth-child(11) {
  animation-delay: 0.75s;
}
.theEpilogue.active .roles-list .role-item:nth-child(12) {
  animation-delay: 0.82s;
}
.theEpilogue.active .roles-list .role-item:nth-child(13) {
  animation-delay: 0.89s;
}

@media (max-width: 768px) {
  .roles-list {
    grid-template-columns: 1fr;
  }
}

.role-section {
  margin-bottom: 1.5rem;
}

.role-section h4 {
  margin-bottom: 0.5rem;
}

.contact-details {
  margin-top: 2rem;
  padding: 1rem;
  border-top: 1px solid var(--border-light);
}

.contact-details p {
  margin: 0.5rem 0;
}

/* Restricted content styling */
.restricted-content {
  margin-top: 2rem;
  padding: 2rem;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--border-radius-medium);
}

/* Simplified restricted content styling */
.restricted-content-simple {
  margin-top: 2rem;
  padding: 1rem;
  border-top: 1px solid var(--border-light);
}

.restricted-message {
  color: var(--text-white);
  text-align: center;
  font-size: var(--smallFontSize);
  font-style: italic;
}
</style>
